import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AdminStatsResponse, DecisionAudit } from '@kkp-fraud/shared';
import { api, type ApiSettings } from '../api';
import { dateTime, ms, num, shortId } from '../format';
import { t } from '../i18n';
import { fill } from './Interpolated';
import { DecisionChip } from './Chips';
import { DECISIONS, StatsTiles, TopRules } from './StatsTiles';
import { Tip } from './PageHead';
import { CardLink, RuleLinks } from './Links';
import { When } from './When';
import { LoadError } from './LoadError';

/**
 * Tab 5 — what the engine has actually been deciding. §7 latency evidence and §6 rule
 * hit counts in one place, so a rule change can be judged by its effect rather than by
 * how it reads.
 */

export function DecisionsTab({ settings }: { settings: ApiSettings }) {
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [rows, setRows] = useState<DecisionAudit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  /** Transaction id, card id (full or part) or rule id — UX review P1-3. */
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(100);
  const [auto, setAuto] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([api.stats(settings), api.decisions(settings, limit)]);
      setStats(s);
      setRows(d);
      setLastRefresh(new Date().toISOString());
      setError(null);
    } catch (e) {
      // As on the Dashboard (QA-M-03): a failed read shows no figures and no rows rather than
      // zeros, "no decisions yet" or the last good answer under the error.
      setStats(null);
      setRows([]);
      setError((e as Error).message);
    }
  }, [settings.baseUrl, limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    if (auto) timer.current = setInterval(() => void refresh(), 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [auto, refresh]);

  /**
   * Newest event time first (QA-M-11). The API's latest-N answer is in ARRIVAL order, and a
   * feeder backlog or a replay delivers old transactions late; the list is headed "Event
   * time", so it is sorted by it. Equal times keep the API's order.
   */
  const byEventTime = useMemo(
    () =>
      rows
        .map((r, i) => ({ r, i, at: Date.parse(r.eventTimeUtc) }))
        .sort((a, b) => (Number.isFinite(b.at) ? b.at : -Infinity) - (Number.isFinite(a.at) ? a.at : -Infinity) || a.i - b.i)
        .map(({ r }) => r),
    [rows],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return byEventTime.filter(
      (r) =>
        (filter === 'all' || r.decision === filter) &&
        (!q || r.transactionId.toLowerCase().includes(q) || r.cardId.toLowerCase().includes(q) || r.reasons.some((id) => id.toLowerCase().includes(q))),
    );
  }, [byEventTime, filter, query]);

  return (
    <>
      <section className="card">
        <header className="card-head">
          <h2>{t('decisions.stats.title')}</h2>
          {stats && <span className="small muted">{t('decisions.stats.subtitle', { n: num(stats.total), since: dateTime(stats.since) })}</span>}
          <Tip>
            {fill(t('decisions.latency.note'), {
              '{metric}': <span className="mono">service_latency_ms</span>,
              '{sample}': <span className="mono">STATS_SAMPLE_SIZE</span>,
            })}
          </Tip>
          <span className="spacer" />
          {lastRefresh && <span className="small muted">{t('decisions.refreshed.label', { at: dateTime(lastRefresh) })}</span>}
          <label className="check small">
            <input type="checkbox" className="toggle" checked={auto} onChange={(e) => setAuto(e.target.checked)} aria-label={t('decisions.autoRefresh.a11y')} />
            {t('decisions.autoRefresh.label')}
          </label>
          <button type="button" className="btn" onClick={() => void refresh()}>
            {t('btn.refresh.label')}
          </button>
        </header>
        <div className="card-body stack">
          {error && <LoadError message={t('log.error.decisions')} detail={error} testId="decisions-error" />}
          {stats ? (
            <StatsTiles stats={stats} aside={{ title: t('decisions.topRules.title'), body: <TopRules byRule={stats.byRule ?? {}} /> }} />
          ) : (
            !error && (
              <div className="empty small" role="status">
                {t('btn.loading.label')}
              </div>
            )
          )}
        </div>
      </section>

      <div>
        <div>
          <section className="card">
            <header className="card-head">
              <h2>{t('decisions.recent.title')}</h2>
              <span className="chip">{t('decisions.shown.chip', { n: visible.length })}</span>
              {/* How to read the row flags — an explanation, so it lives in the tooltip, not on the page. */}
              <Tip label={t('decisions.reading.title')}>
                <strong>{t('flag.degraded.chip')}</strong> {t('decisions.reading.degraded')}{' '}
                <strong>{t('flag.shadow.chip')}</strong> {t('decisions.reading.shadow', { action: 'APPROVE' })}{' '}
                {t('decisions.reading.ids')}
              </Tip>
              <span className="spacer" />
              <input
                type="search"
                aria-label={t('decisions.search.a11y')}
                placeholder={t('decisions.search.placeholder')}
                style={{ width: 220 }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="decisions-search"
              />
              <select aria-label={t('decisions.filter.a11y')} value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 160 }}>
                <option value="all">{t('decisions.filter.all')}</option>
                {DECISIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <select aria-label={t('decisions.limit.a11y')} value={limit} onChange={(e) => setLimit(Number(e.target.value))} style={{ width: 110 }}>
                {[50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>
                    {t('decisions.limit.option', { n })}
                  </option>
                ))}
              </select>
            </header>
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>{t('decisions.col.time')}</th>
                    <th style={{ width: 110 }}>{t('decisions.col.txn')}</th>
                    <th style={{ width: 96 }}>{t('log.col.card')}</th>
                    <th style={{ width: 128 }}>{t('col.decision.label')}</th>
                    <th>{t('decisions.col.reasons')}</th>
                    <th className="num-cell" style={{ width: 92 }}>
                      {t('decisions.col.amount')}
                    </th>
                    <th className="num-cell" style={{ width: 82 }}>
                      {t('decisions.col.latency')}
                    </th>
                    <th style={{ width: 120 }}>{t('decisions.col.flags')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!lastRefresh && !error && (
                    <tr>
                      <td colSpan={8} className="empty" role="status">
                        {t('btn.loading.label')}
                      </td>
                    </tr>
                  )}
                  {visible.length === 0 && rows.length > 0 && (
                    <tr>
                      <td colSpan={8} className="empty">
                        {t('log.search.none')}
                      </td>
                    </tr>
                  )}
                  {rows.length === 0 && !error && lastRefresh && (
                    <tr>
                      <td colSpan={8} className="empty">
                        {t('decisions.empty.note')}
                      </td>
                    </tr>
                  )}
                  {visible.map((r) => (
                    <tr key={r.id} data-testid={`decision-${r.transactionId}`}>
                      <td className="mono small nowrap">
                        <When iso={r.eventTimeUtc} />
                      </td>
                      <td className="mono small nowrap" title={r.transactionId}>
                        {shortId(r.transactionId, 10)}
                      </td>
                      <td className="mono small nowrap">
                        <CardLink cardId={r.cardId} testid={`decision-card-${r.transactionId}`} />
                      </td>
                      <td>
                        <DecisionChip decision={r.decision} />
                      </td>
                      <td>
                        <RuleLinks ids={r.reasons} />
                      </td>
                      <td className="num-cell">{num(r.amountThb, 2)}</td>
                      <td className="num-cell small">{ms(r.serviceLatencyMs)}</td>
                      <td>
                        <div className="row-wrap">
                          {r.degraded && (
                            <span className="chip warn" title={t('decisions.degraded.tip')}>
                              {t('flag.degraded.chip')}
                            </span>
                          )}
                          {r.shadowMode && (
                            <span className="chip" title={t('decisions.shadow.tip', { action: 'APPROVE' })}>
                              {t('flag.shadow.chip')}
                            </span>
                          )}
                          {r.network && <span className="chip">{r.network}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

      </div>
    </>
  );
}
